import React, { useState, useEffect } from 'react';

// Placeholder for icons - consider using react-icons or custom SVGs
// import { FaCheckCircle, FaRegCircle } from 'react-icons/fa';

const StepTwoServiceDetails = ({ onNext, onPrevious, formData }) => {
  const [detailsData, setDetailsData] = useState(formData.stepTwoDetails || {});
  const [describeOther, setDescribeOther] = useState(false);

  const serviceType = formData.serviceType; // From Step 1

  // --- Common State Management ---
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox') {
      // Handle multi-select checkboxes (e.g., common issues)
      setDetailsData(prev => ({
        ...prev,
        [name]: prev[name] ? 
          (checked ? [...prev[name], value] : prev[name].filter(item => item !== value)) 
          : (checked ? [value] : [])
      }));
      if (name === 'commonIssues' && value === 'Other' && checked) {
        setDescribeOther(true);
      } else if (name === 'commonIssues' && value === 'Other' && !checked) {
        setDescribeOther(false);
        // Optionally clear the otherDescription when "Other" is unchecked
        setDetailsData(prev => ({ ...prev, otherDescription: '' }));
      }
    } else {
      setDetailsData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleAddonChange = (e) => {
    const { name, checked } = e.target;
    setDetailsData(prev => ({
        ...prev,
        addOns: {
            ...prev.addOns,
            [name]: checked
        }
    }));
  };
  
  const handleTierSelection = (tierName) => {
    setDetailsData(prev => ({ ...prev, selectedTier: tierName }));
  };

  // --- Validation (Simplified) ---
  const canProceed = () => {
    if (isRepairType) {
      return detailsData.commonIssues && detailsData.commonIssues.length > 0;
    } else if (isInstallationType) {
      return detailsData.installationType && detailsData.propertySize && detailsData.propertyType && detailsData.selectedTier;
    }
    return true; // Default for other service types not yet detailed
  };

  const handleNext = () => {
    if (canProceed()) {
      onNext({ stepTwoDetails: detailsData });
    } else {
      // TODO: Implement more robust validation feedback
      alert('Please fill in all required fields for this step.');
    }
  };
  
  // --- Dynamic Content Based on Service Type ---
  // These would ideally come from a config or API
  const isRepairType = serviceType && serviceType.toLowerCase().includes('repair');
  const isMaintenanceType = serviceType && serviceType.toLowerCase().includes('maintenance');
  const isInstallationType = serviceType && serviceType.toLowerCase().includes('installation');

  // --- Scenario A: Repair/Maintenance Specific Fields ---
  const repairIssues = [
    "Not Cooling / Blowing Warm Air",
    "Strange Noises",
    "Leaking Water",
    "Unit Won't Turn On",
    "Thermostat Issues",
    "Routine Maintenance Request",
    "Other"
  ];
  const systemAges = ["0-5 years", "6-10 years", "10+ years", "Unsure"];
  const systemTypes = ["Central AC", "Ductless Mini-Split", "Heat Pump", "Unsure"];
  const repairAddOns = [
      { name: "Capacitor Replacement", price: "Approx. $150 - $300", key: "capacitor" },
      { name: "System Tune-Up & Cleaning", price: "$99", key: "tuneUp" },
      { name: "Smart Thermostat Installation", price: "Starting from $250", key: "smartThermostat" }
  ];
  const DIAGNOSTIC_FEE = "89.00";


  // --- Scenario B: Installation Specific Fields ---
  const installationTypes = ["New System Installation (First Time)", "Replacing Existing System"];
  const propertySizes = ["Under 1500 sq ft", "1500-2500 sq ft", "2500-3500 sq ft", "Over 3500 sq ft"];
  const propertyTypes = ["Single-Family Home", "Townhouse", "Condo"];
  const systemTiers = [
    { 
      name: "Standard Efficiency", 
      features: ["Up to 14 SEER Rating", "5-Year Parts Warranty", "Basic Thermostat Compatible"],
      price: "Starting from $3,500",
      key: "standard"
    },
    { 
      name: "High Performance", 
      features: ["16-18 SEER Rating", "10-Year Parts Warranty", "Wi-Fi Thermostat Included"],
      price: "Starting from $5,500",
      key: "performance"
    },
    { 
      name: "Premium Comfort & Efficiency", 
      features: ["20+ SEER Rating / Variable Speed", "10-Year Parts & Labor Warranty", "Advanced Smart Home Integration"],
      price: "Starting from $8,000",
      key: "premium"
    }
  ];
    const installationAddOns = [
      { name: "Smart Thermostat Upgrade", price: "+$150", key: "smartThermostatUpgrade" },
      { name: "Whole-Home Air Purifier", price: "+$800", key: "airPurifier" },
      { name: "Extended Warranty Plan (10 Year Labor)", price: "+$500", key: "extendedWarranty" }
  ];


  // Effect to initialize detailsData based on serviceType if it's empty
  useEffect(() => {
    if (Object.keys(detailsData).length === 0) {
        if (isRepairType) {
            setDetailsData({ commonIssues: [], systemAge: '', systemType: '', otherDescription: '', addOns: {} });
        } else if (isInstallationType) {
            setDetailsData({ installationType: '', propertySize: '', propertyType: '', selectedTier: '', addOns: {} });
        }
        // Add other conditions for different service types if needed
    }
    // Reset describeOther based on current data
    if (detailsData.commonIssues && detailsData.commonIssues.includes('Other')) {
        setDescribeOther(true);
    } else {
        setDescribeOther(false);
    }
  }, [serviceType, isRepairType, isInstallationType, detailsData]);


  return (
    <div className="space-y-6">
      <h3 className="text-xl font-semibold text-brand-text-dark mb-4">Provide More Details</h3>

      {/* --- SCENARIO A: REPAIR/MAINTENANCE --- */}
      {(isRepairType || isMaintenanceType) && (
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-brand-text-light mb-2">What specific problems are you experiencing? (Select all that apply)</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {repairIssues.map(issue => (
                <label key={issue} className="flex items-center p-3 border border-gray-300 rounded-md hover:border-brand-accent transition-all">
                  <input 
                    type="checkbox" 
                    name="commonIssues" 
                    value={issue}
                    checked={detailsData.commonIssues?.includes(issue) || false}
                    onChange={handleChange}
                    className="h-5 w-5 text-brand-accent-orange border-gray-300 rounded focus:ring-brand-accent-orange mr-3"
                  />
                  <span className="text-sm text-brand-text-dark">{issue}</span>
                </label>
              ))}
            </div>
          </div>

          {describeOther && (
            <div>
              <label htmlFor="otherDescription" className="block text-sm font-medium text-brand-text-light mb-1">Please describe the "Other" issue:</label>
              <textarea
                id="otherDescription"
                name="otherDescription"
                rows="3"
                value={detailsData.otherDescription || ''}
                onChange={handleChange}
                className="mt-1 block w-full p-3 border border-gray-300 rounded-md shadow-sm focus:ring-brand-accent focus:border-brand-accent sm:text-sm"
                placeholder="e.g., The unit smells musty when it first turns on."
              />
            </div>
          )}
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="systemAge" className="block text-sm font-medium text-brand-text-light mb-1">Approximate age of your system? (Optional)</label>
              <select 
                id="systemAge" 
                name="systemAge" 
                value={detailsData.systemAge || ''} 
                onChange={handleChange}
                className="mt-1 block w-full p-3 border border-gray-300 rounded-md shadow-sm focus:ring-brand-accent focus:border-brand-accent sm:text-sm"
              >
                <option value="">Unsure / Not Applicable</option>
                {systemAges.map(age => <option key={age} value={age}>{age}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="systemType" className="block text-sm font-medium text-brand-text-light mb-1">Type of system? (Optional)</label>
              <select 
                id="systemType" 
                name="systemType" 
                value={detailsData.systemType || ''} 
                onChange={handleChange}
                className="mt-1 block w-full p-3 border border-gray-300 rounded-md shadow-sm focus:ring-brand-accent focus:border-brand-accent sm:text-sm"
              >
                <option value="">Unsure / Not Applicable</option>
                {systemTypes.map(type => <option key={type} value={type}>{type}</option>)}
              </select>
            </div>
          </div>

          <div className="p-4 bg-brand-green-lightest rounded-md border border-brand-green-light">
            <h4 className="text-md font-semibold text-brand-green-dark mb-1">Diagnostic Fee</h4>
            <p className="text-sm text-brand-text-dark">
              Our standard diagnostic fee is <span className="font-bold">${DIAGNOSTIC_FEE}</span>. 
              This covers the technician's travel and time to thoroughly diagnose the issue. 
              The diagnostic fee will be <span className="font-semibold">waived</span> if you proceed with the recommended repairs.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-brand-text-light mb-2">Consider these common solutions or upgrades (optional):</label>
            <div className="space-y-2">
              {repairAddOns.map(addon => (
                <label key={addon.key} className="flex items-center p-3 border border-gray-200 rounded-md">
                  <input 
                    type="checkbox" 
                    name={addon.key}
                    checked={detailsData.addOns?.[addon.key] || false}
                    onChange={handleAddonChange}
                    className="h-5 w-5 text-brand-accent-orange border-gray-300 rounded focus:ring-brand-accent-orange mr-3"
                  />
                  <div>
                    <span className="text-sm text-brand-text-dark">{addon.name}</span>
                    <p className="text-xs text-brand-text-light">{addon.price}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* --- SCENARIO B: NEW SYSTEM INSTALLATION --- */}
      {isInstallationType && (
        <div className="space-y-8">
          <div>
  <label className="block text-sm font-medium text-brand-text-light mb-2">
    What type of installation are you looking for?
  </label>
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
    {installationTypes.map(type => (
      <label
        key={type}
        className={`flex items-center p-4 border rounded-lg cursor-pointer transition-all duration-200 ${
          detailsData.installationType?.includes(type)
            ? 'border-brand-accent-orange ring-2 ring-brand-accent-orange bg-orange-50'
            : 'border-gray-300 hover:border-gray-400'
        }`}
      >
        <input
          type="checkbox"
          name="installationType"
          value={type}
          checked={detailsData.installationType?.includes(type) || false}
          onChange={handleChange}
          className="h-5 w-5 text-brand-accent-orange border-gray-300 rounded focus:ring-brand-accent-orange mr-3"
        />
        <span className="text-sm font-medium text-brand-text-dark">{type}</span>
      </label>
    ))}
  </div>
</div>

          <div>
            <label className="block text-sm font-medium text-brand-text-light mb-2">Tell us about your property:</label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="propertySize" className="block text-xs font-medium text-brand-text-light mb-1">Home Size (approx.)</label>
                <select id="propertySize" name="propertySize" value={detailsData.propertySize || ''} onChange={handleChange} className="mt-1 block w-full p-3 border border-gray-300 rounded-md shadow-sm focus:ring-brand-accent focus:border-brand-accent sm:text-sm">
                  <option value="" disabled>Select size</option>
                  {propertySizes.map(size => <option key={size} value={size}>{size}</option>)}
                </select>
              </div>
              <div>
                <label htmlFor="propertyType" className="block text-xs font-medium text-brand-text-light mb-1">Property Type</label>
                <select id="propertyType" name="propertyType" value={detailsData.propertyType || ''} onChange={handleChange} className="mt-1 block w-full p-3 border border-gray-300 rounded-md shadow-sm focus:ring-brand-accent focus:border-brand-accent sm:text-sm">
                  <option value="" disabled>Select type</option>
                  {propertyTypes.map(type => <option key={type} value={type}>{type}</option>)}
                </select>
              </div>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-brand-text-light mb-3">Choose a system level that fits your needs and budget (estimates include standard installation):</label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {systemTiers.map(tier => (
                <div 
                    key={tier.key} 
                    onClick={() => handleTierSelection(tier.key)}
                    className={`p-4 border rounded-lg cursor-pointer transition-all duration-200 flex flex-col justify-between ${detailsData.selectedTier === tier.key ? 'border-brand-accent-orange ring-2 ring-brand-accent-orange bg-orange-50' : 'border-gray-300 hover:border-gray-400 hover:shadow-md'}`}
                >
                  <div>
                    <h4 className="text-md font-semibold text-brand-green-medium mb-2">{tier.name}</h4>
                    <ul className="list-disc list-inside text-xs text-brand-text-light space-y-1 mb-3">
                      {tier.features.map(feature => <li key={feature}>{feature}</li>)}
                    </ul>
                    <p className="text-sm font-semibold text-brand-text-dark mb-3">{tier.price}</p>
                  </div>
                  <button 
                    type="button"
                    onClick={(e) => { e.stopPropagation(); handleTierSelection(tier.key); }}
                    className={`w-full text-sm py-2 px-3 rounded-md transition-colors ${detailsData.selectedTier === tier.key ? 'bg-brand-accent-orange text-white' : 'bg-gray-200 hover:bg-gray-300 text-brand-text-dark'}`}
                  >
                    {detailsData.selectedTier === tier.key ? 'Selected' : 'Select Plan'}
                  </button>
                  {/* <p className="text-xs text-blue-600 hover:underline mt-2 text-center">Learn More</p> */}
                </div>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-brand-text-light mb-2">Popular Add-ons for New Systems (optional):</label>
            <div className="space-y-2">
              {installationAddOns.map(addon => (
                <label key={addon.key} className="flex items-center p-3 border border-gray-200 rounded-md">
                  <input 
                    type="checkbox" 
                    name={addon.key}
                    checked={detailsData.addOns?.[addon.key] || false}
                    onChange={handleAddonChange}
                    className="h-5 w-5 text-brand-accent-orange border-gray-300 rounded focus:ring-brand-accent-orange mr-3"
                  />
                  <div>
                    <span className="text-sm text-brand-text-dark">{addon.name}</span>
                    <p className="text-xs text-brand-text-light">{addon.price}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Fallback for service types not yet detailed */}
      {!isRepairType && !isMaintenanceType && !isInstallationType && (
        <div>
            <p className="text-brand-text-light">Detailed options for "{serviceType}" will be discussed during our confirmation call.</p>
            <label htmlFor="generalNotes" className="block text-sm font-medium text-brand-text-light mt-4 mb-1">You can add any specific notes here:</label>
            <textarea
                id="generalNotes"
                name="generalNotes"
                rows="4"
                value={detailsData.generalNotes || ''}
                onChange={handleChange}
                className="mt-1 block w-full p-3 border border-gray-300 rounded-md shadow-sm focus:ring-brand-accent focus:border-brand-accent sm:text-sm"
                placeholder="e.g., I'm interested in financing options, or specific model numbers."
            />
        </div>
      )}


      <div className="flex justify-between mt-8">
        <button 
          type="button" 
          onClick={onPrevious} 
          className="cta-button-secondary"
        >
          Previous
        </button>
        <button 
          type="button" 
          onClick={handleNext} 
          // disabled={!canProceed()} // Enable validation when ready
          className="cta-button disabled:opacity-50"
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default StepTwoServiceDetails; 